import React, { useState, useEffect } from 'react';
import { Briefcase } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const ENTITY_TYPES = [
  'Individual Investor',
  'HUF (Hindu Undivided Family)',
  'Spouse',
  'Minor / Child',
  'Partnership Firm',
  'Private Company',
  'Trust',
  'Other',
];

const PortfolioModal = ({ isOpen, onClose, onSubmit, editingPortfolio }) => {
  const [formData, setFormData] = useState({ name: '', entityType: 'Individual Investor', panNumber: '' });

  useEffect(() => {
    if (editingPortfolio) {
      setFormData({
        name: editingPortfolio.name || '',
        entityType: editingPortfolio.entityType || 'Individual Investor',
        panNumber: editingPortfolio.panNumber || '',
      });
    } else {
      setFormData({ name: '', entityType: 'Individual Investor', panNumber: '' });
    }
  }, [editingPortfolio, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-orange-600" />
            {editingPortfolio ? 'Edit Portfolio' : 'New Portfolio'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label htmlFor="pname" className="text-slate-700">Portfolio Name *</Label>
            <Input
              id="pname"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g., Self, Spouse, HUF"
              required
              className="border-slate-300 focus:border-orange-500"
              data-testid="portfolio-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ptype" className="text-slate-700">Entity Type</Label>
            <Select value={formData.entityType} onValueChange={(v) => setFormData((p) => ({ ...p, entityType: v }))}>
              <SelectTrigger className="border-slate-300 focus:border-orange-500" data-testid="portfolio-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ppan" className="text-slate-700">PAN Number</Label>
            <Input
              id="ppan"
              value={formData.panNumber}
              onChange={(e) => setFormData((p) => ({ ...p, panNumber: e.target.value.toUpperCase() }))}
              placeholder="e.g., ABCDE1234F"
              maxLength={10}
              className="border-slate-300 focus:border-orange-500"
              data-testid="portfolio-pan-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300 hover:bg-slate-100">
              Cancel
            </Button>
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="portfolio-submit-btn">
              {editingPortfolio ? 'Update' : 'Create'} Portfolio
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioModal;
